import React, { useLayoutEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigationType } from 'react-router-dom';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import ActAsBanner from './ActAsBanner';
import { useOrganisation } from '@/lib/OrganisationContext';
import { useAuth } from '@/lib/AuthContext';
import Suspended from '@/pages/Suspended';
import { moveFocusForRoute } from '@/lib/routeFocus';
import AskDai from '@/components/askdai/AskDai';
import { isAskDaiNavigation } from '@/components/askdai/navigation';

const pageTitles = {
  '/': 'Dashboard',
  '/assessment': 'Maturity Assessment',
  '/roadmap': 'Roadmap',
  '/evidence-vault': 'Evidence Vault',
  '/resources': 'Resource Library',
  '/settings': 'Settings',
  '/slt': 'Leadership',
  '/personas': 'Personas',
  '/esg-bids': 'ESG & Bids',
};

export default function AppLayout() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const mainRef = useRef(null);
  const handledRouteKeyRef = useRef(null);
  const handledLocationRef = useRef(null);
  const scrollPositionsRef = useRef(new Map());
  const tenantBlockedRef = useRef(false);
  const pendingSkipRef = useRef(false);
  const menuOriginKeysRef = useRef(new Set());
  const currentRouteKeyRef = useRef(location.key);
  currentRouteKeyRef.current = location.key;
  const { selectedOrg, loading, organisationError, tenantTransitioning, tenantRevision, reloadOrganisations } = useOrganisation();
  const { logout } = useAuth();
  const title = location.pathname.startsWith('/admin') ? 'DI Console' : (pageTitles[location.pathname] || 'DI Disability Support');
  const busy = loading || tenantTransitioning;
  const tenantBlocked = busy || !!organisationError || !selectedOrg;
  const suspended = selectedOrg?.status === 'suspended';
  tenantBlockedRef.current = tenantBlocked || suspended;
  const recoveryTitle = organisationError ? 'Organisation access needs recovery' : busy ? 'Loading organisation' : 'No organisation access';

  useLayoutEffect(() => {
    // This layout restores its own scroll container. Native history restoration
    // would otherwise reapply a URL fragment after the dialog's Back transition.
    window.history.scrollRestoration = 'manual';
    return () => { window.history.scrollRestoration = 'auto'; };
  }, []);

  useLayoutEffect(() => {
    const main = mainRef.current;
    if (!main || tenantBlocked || suspended) return undefined;

    let cancelScrollRestore;
    const chatNavigation = isAskDaiNavigation(handledLocationRef.current, location);
    handledLocationRef.current = location;
    if (pendingSkipRef.current) {
      pendingSkipRef.current = false;
      moveFocusForRoute(main, { scrollTop: 0 });
    }
    if (handledRouteKeyRef.current === null) {
      handledRouteKeyRef.current = location.key;
    } else if (handledRouteKeyRef.current !== location.key) {
      const scrollTop = navigationType === 'POP'
        ? (scrollPositionsRef.current.get(location.key) ?? 0)
        : 0;
      if (!chatNavigation) {
        cancelScrollRestore = moveFocusForRoute(main, {
          scrollTop,
          restoreWhenReady: navigationType === 'POP',
        });
      } else {
        scrollPositionsRef.current.set(location.key, main.scrollTop);
      }
      handledRouteKeyRef.current = location.key;
      if (!chatNavigation && navigationType === 'POP' && menuOriginKeysRef.current.has(location.key)) {
        const restoredKey = location.key;
        requestAnimationFrame(() => {
          if (currentRouteKeyRef.current === restoredKey) window.dispatchEvent(new Event('inclusivecore:focus-main-menu'));
        });
      }
    }

    const routeKey = location.key;
    const rememberScroll = () => {
      scrollPositionsRef.current.set(routeKey, main.scrollTop);
    };
    main.addEventListener('scroll', rememberScroll, { passive: true });
    return () => {
      cancelScrollRestore?.();
      main.removeEventListener('scroll', rememberScroll);
    };
  }, [location, navigationType, suspended, tenantBlocked]);

  useLayoutEffect(() => {
    const rememberMenuOrigin = (event) => { if (event.detail?.originKey) menuOriginKeysRef.current.add(event.detail.originKey); };
    window.addEventListener('inclusivecore:menu-route', rememberMenuOrigin);
    return () => window.removeEventListener('inclusivecore:menu-route', rememberMenuOrigin);
  }, []);

  useLayoutEffect(() => {
    const skipLink = document.querySelector('.skip-link[href="#main-content"]');
    if (!skipLink) return undefined;
    const skip = (event) => {
      event.preventDefault();
      if (tenantBlockedRef.current) pendingSkipRef.current = true;
      moveFocusForRoute(mainRef.current, { scrollTop: 0 });
    };
    skipLink.addEventListener('click', skip);
    return () => skipLink.removeEventListener('click', skip);
  }, []);

  return (
    <div className="flex h-screen flex-col bg-[#111111]">
      <button type="button" onClick={() => window.dispatchEvent(new Event('inclusivecore:focus-main-menu'))} className="fixed left-3 top-3 z-[100] -translate-y-24 rounded bg-[#FFB800] px-4 py-2 font-semibold text-black focus:translate-y-0">Back to main menu</button>
      <ActAsBanner />
      {tenantBlocked || suspended ? (
        <main ref={mainRef} id="main-content" tabIndex={-1} aria-label={suspended && !tenantBlocked ? 'Organisation suspended' : recoveryTitle} className="min-h-0 flex-1 overflow-y-auto bg-[#111111] p-6">
          {suspended && !tenantBlocked ? <Suspended /> : (
            <section className="mx-auto max-w-xl rounded-lg border border-gray-600 bg-[#1a1a1a] p-6 text-center sm:p-8" aria-labelledby="organisation-recovery-title" aria-busy={busy}>
              <h1 id="organisation-recovery-title" className="text-2xl font-semibold text-white">{recoveryTitle}</h1>
              <p className="mt-4 text-white/80" role={organisationError ? 'alert' : undefined}>
                {organisationError || (busy ? 'Confirming your organisation access…' : 'You no longer have access to an organisation. Contact Diverse Integration for help, or check again if your access has changed.')}
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <button type="button" disabled={busy} className="min-h-11 rounded border border-gray-600 px-4 py-2 text-sm text-white/80 hover:bg-white/10 disabled:opacity-50" onClick={() => reloadOrganisations().catch(() => {})}>Try again</button>
                <button type="button" className="min-h-11 rounded border border-gray-600 px-4 py-2 text-sm text-white/80 hover:bg-white/10" onClick={() => logout()}>Sign out</button>
              </div>
            </section>
          )}
        </main>
      ) : <div className="flex min-h-0 flex-1">
        <Sidebar />
        <div className="flex min-w-0 flex-1 flex-col">
          <TopBar title={title} />
          <main
            ref={mainRef}
            id="main-content"
            tabIndex={-1}
            role="main"
            aria-label={title}
            onClickCapture={() => menuOriginKeysRef.current.delete(location.key)}
            className="flex-1 overflow-y-auto bg-[#111111] p-6"
          >
            <button type="button" onClick={event => window.dispatchEvent(new CustomEvent('inclusivecore:ask-dai-open', { detail: { invoker: event.currentTarget } }))} className="ask-dai-entry mb-4 min-h-11 rounded-lg border border-teal-600 bg-teal-800 px-4 py-2 font-semibold text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#FFB800]">Ask Dai</button>
            <div key={tenantRevision} data-route-content><Outlet /></div>
          </main>
        </div>
      </div>}
      {!tenantBlocked && !suspended && <AskDai key={selectedOrg?.id || 'no-organisation'} />}
    </div>
  );
}
